/**
 * Payment Service
 * Handles all payment and transaction-related API calls
 */

import { apiGet, apiPost } from './apiClient';
import API_CONFIG from './apiConfig';

/**
 * Get active payment methods with limits, fees, and system status
 * @returns {Promise} { success, data: { paymentMethods, limits, fees, systemStatus, verificationStatus }, message }
 */
export const getPaymentMethods = async () => {
  return apiGet(API_CONFIG.endpoints.paymentMethods);
};

/**
 * Create a deposit request
 * @param {Object} data - { amount: number, paymentMethodId: string, medium: string, transactionId: string }
 * @returns {Promise} { success, data: { depositId, reference, amount, status, medium, createdAt }, message }
 */
export const createDeposit = async (data) => {
  const payload = {
    amount: Number(data.amount),
    paymentMethodId: data.paymentMethodId,
    medium: data.medium,
    transactionId: data.transactionId
  };
  return apiPost(API_CONFIG.endpoints.deposit, payload);
};

/**
 * Create a withdrawal request
 * @param {Object} data - { amount: number, bankAccountId: string, medium: string }
 * @returns {Promise} { success, data: { withdrawalId, reference, amount, status, medium }, message }
 */
export const createWithdraw = async (data) => {
  const payload = {
    amount: Number(data.amount),
    bankAccountId: data.bankAccountId,
    medium: data.medium
  };
  return apiPost(API_CONFIG.endpoints.withdraw, payload);
};

/**
 * Create a withdrawal request (alias for createWithdraw for backward compatibility)
 * @param {Object} data - { amount: number, bankAccountId: string, remarks?: string }
 * @returns {Promise}
 */
export const createWithdrawal = async (data) => {
  return createWithdraw(data);
};

/**
 * Get user's transaction history with optional filters
 * @param {Object} params - Optional query parameters { type, status, startDate, endDate, page, limit, sortBy, sortOrder }
 * @returns {Promise} { success, data: { transactions, pagination, summary }, message }
 */
export const getTransactions = async (params = {}) => {
  // Build query string from params
  const queryParams = new URLSearchParams();
  
  if (params.type) queryParams.append('type', params.type);
  if (params.status) queryParams.append('status', params.status);
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);
  if (params.page) queryParams.append('page', params.page);
  if (params.limit) queryParams.append('limit', params.limit);
  if (params.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  
  const endpoint = API_CONFIG.endpoints.transactions + (queryParams.toString() ? `?${queryParams.toString()}` : '');
  return apiGet(endpoint);
};

